import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogSettingsUsersAdd } from 'app/dialogs/settings/users/add/dialog-settings-users-add';
import { DialogSettingsUsersDetails } from 'app/dialogs/settings/users/details/dialog-settings-users-details';
import { formatKennitala } from 'app/helpers/formatKennitala';
import { ExposedListDocumentTemplatesReq } from 'app/interfaces/requests/Exposed/exposedListDocumentTemplatesReq';
import { ExposedListLogsExternalSystemsRes } from 'app/interfaces/responses/Exposed/exposedExternalSystemDetailsRes';
import { ExposedListEmployeeDivisionsRes } from 'app/interfaces/responses/Exposed/exposedListEmployeeDivisionsRes';
import { ExposedListPowerBIReportsRes } from 'app/interfaces/responses/Exposed/exposedListPowerBIReportsRes';
import { ExposedListUsersRes } from 'app/interfaces/responses/Exposed/exposedListUsersRes';
import { ListConfigContextsRes } from 'app/interfaces/responses/List/listConfigContextsRes';
import { ListDocumentTemplatesRes } from 'app/interfaces/responses/List/listDocumentTemplatesRes';
import { ListRoleGroupsRes } from 'app/interfaces/responses/List/listRoleGroupsRes';
import { ListRolesRes } from 'app/interfaces/responses/List/listRolesRes';
import { ListTranslationContextsRes } from 'app/interfaces/responses/List/listTranslationContextsRes';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { LogsService } from 'app/services/api/logs/logs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { PowerBIService } from 'app/services/api/powerbi/powerbi.service';
import { SkjalamidjaService } from 'app/services/api/skjalamidja/skjalamidja.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import latinize from 'latinize';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

@Component({
    selector: 'app-settings-users',
    templateUrl: './settings-users.component.html',
    styleUrls: ['./settings-users.component.scss'],
})
export class SettingsUsersComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('userRow', { read: ElementRef })
    userRows!: QueryList<ElementRef>;

    @ViewChild('sentinel', { static: false })
    sentinel!: ElementRef;

    drawerMode: 'side' | 'over';
    usersCount: number = 0;
    searchInputControl: UntypedFormControl = new UntypedFormControl();
    selectedUser: ExposedListUsersRes;

    searchedUsers: Array<ExposedListUsersRes> = [];
    users: Array<ExposedListUsersRes> = [];
    nextCursor: string;
    hasHttpFetched: boolean = false;
    isLoading: boolean = false;
    pageSize: number = 50;
    currentSearch: string = '';
    private observer!: IntersectionObserver;

    roleGroups: ListRoleGroupsRes[] = [];
    translationContexts: ListTranslationContextsRes[] = [];
    roles: ListRolesRes[] = [];
    configContexts: ListConfigContextsRes[] = [];
    availablePowerBIReports: ExposedListPowerBIReportsRes[] = [];
    documentTemplates: ListDocumentTemplatesRes[] = [];
    employeeDivisions: ExposedListEmployeeDivisionsRes[] = [];
    externalSystems: ExposedListLogsExternalSystemsRes[];

    userRoles = USER_ROLES;

    constructor(
        public lsrauthService: LsrauthService,
        public configsService: ConfigsService,
        public translationService: TranslationService,
        public powerBIService: PowerBIService,
        private _activatedRoute: ActivatedRoute,
        private skjalamidjaService: SkjalamidjaService,
        private logsService: LogsService,
        private _router: Router,
        public dialog: MatDialog,
        @Inject(DOCUMENT) private _document: any
    ) {}

    ngOnInit(): void {
        this.getUsers(true);
        this.getUserTranslationContexts();
        this.getRoles();
        this.getUserConfigContexts();
        this.getAvailablePowerBIReports();
        this.getDocumentTemplates();
        this.getEmployeeDivisions();
        this.getExternalSystems();
        this.searchUsers();
    }

    ngAfterViewInit() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (this.nextCursor && !this.isLoading) {
                        this.observer.unobserve(entry.target);
                        this.getUsers(false);
                    } else if (!this.nextCursor) {
                        this.observer.disconnect();
                    }
                }
            });
        });

        // Whenever the rendered rows change (new page added), re‑observe the sentinel
        this.userRows.changes.subscribe(() => {
            this._observeSentinel();
        });

        // Initial observation (in case rows already present)
        this._observeSentinel();
    }

    private _observeSentinel() {
        if (this.sentinel) {
            this.observer.observe(this.sentinel.nativeElement);
        }
    }

    ngOnDestroy() {
        this.observer.disconnect();
    }

    getUsers(reset: boolean = false) {
        if (this.isLoading) return;
        if (reset) {
            this.users = [];
            this.nextCursor = null;
        }
        this.isLoading = true;

        this.lsrauthService.listEmployees(this.nextCursor, this.currentSearch).subscribe(
            (page) => {
                this.isLoading = false;
                this.users = [...this.users, ...page.items];
                this.searchedUsers = this.users;
                this.nextCursor = page.nextCursor;
                this.usersCount = page.totalCount;
            },
            (err) => {
                console.error('Error loading employees ', err);
                this.isLoading = false;
            }
        );
    }

    searchUsers() {
        this.searchInputControl.valueChanges
            .pipe(
                map((v) => (v ?? '').toString().trim()),
                debounceTime(500),
                distinctUntilChanged()
            )
            .subscribe((term) => {
                this.currentSearch = term.trim();
                this.getUsers(true);
            });
    }

    trackByFn(_: number, user: ExposedListUsersRes) {
        return user.kennitala;
    }

    getUserTranslationContexts() {
        this.translationService.listTranslationContexts().subscribe((res) => {
            this.translationContexts = res;
        });
    }

    getRoles() {
        this.lsrauthService.getUserAccessRoles().then((value) => {
            this.roleGroups = value.roleGroups;
            this.roles = value.roles;
        });
    }

    getUserConfigContexts() {
        this.configsService.listConfigContexts().subscribe((res) => {
            this.configContexts = res;
        });
    }

    getAvailablePowerBIReports() {
        this.powerBIService.getReportList(true, false).subscribe((res) => {
            this.availablePowerBIReports = res;
        });
    }

    getDocumentTemplates() {
        this.skjalamidjaService
            .listDocumentTemplates(true, {
                documentTemplateId: null,
                templateKey: null,
                templateTranslationKey: null,
                filterByCurrentUser: false,
            } as ExposedListDocumentTemplatesReq)
            .subscribe((res) => {
                this.documentTemplates = res;
            });
    }

    getEmployeeDivisions() {
        this.lsrauthService.getEmployeeDivisions().then((res) => (this.employeeDivisions = res));
    }

    getExternalSystems() {
        this.logsService.getExternalSystemDetails().subscribe((res) => {
            this.externalSystems = res.externalSystems;
        });
    }

    formatKennitala(kt) {
        if (kt) {
            return formatKennitala(kt);
        } else {
            return '';
        }
    }

    viewUserDetails(user: ExposedListUsersRes) {
        const dialogRef = this.dialog.open(DialogSettingsUsersDetails, {
            autoFocus: false,
            restoreFocus: false,
            width: '550px',
            data: {
                user: user,
                roleGroups: this.roleGroups,
                translationContexts: this.translationContexts,
                roles: this.roles,
                configContexts: this.configContexts,
                availablePowerBIReports: this.availablePowerBIReports,
                documentTemplates: this.documentTemplates,
                employeeDivisions: this.employeeDivisions,
                externalSystems: this.externalSystems,
            },
        });

        dialogRef.componentInstance.onConfirm.subscribe((emitValue) => {
            if (emitValue) {
                this.getUsers(true);
            }
        });
    }

    addUser() {
        const dialogRef = this.dialog.open(DialogSettingsUsersAdd, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {},
        });

        dialogRef.componentInstance.onConfirm.subscribe((emitValue) => {
            if (emitValue) {
                this.getUsers(true);
            }
        });
    }

    sortUsers(a: ExposedListUsersRes, b: ExposedListUsersRes) {
        if (latinize(a.displayName) < latinize(b.displayName)) {
            return -1;
        }
        if (latinize(a.displayName) > latinize(b.displayName)) {
            return 1;
        }
        return 0;
    }

    /**
     * On backdrop clicked
     */
    onBackdropClicked(): void {
        // Go back to the list
        this._router.navigate(['./'], { relativeTo: this._activatedRoute });
    }
}
